<template>
  <BaseModal :modalTitle="$t('W: Videresend prøver')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiComboboxVee
        name="fromLocationID"
        :label="$t('W: Fra')"
        :placeholder="$t('W: Vælg lokation')"
        :options="locationOptions"
        :rules="['required']"
        isRequired
        v-model="form.fromLocationID"
        @update:model-value="errorText = ''" />

      <UiComboboxVee
        name="toLocationID"
        :label="$t('W: Til')"
        :placeholder="$t('W: Vælg lokation')"
        :options="toLocationOptions"
        :rules="['required']"
        isRequired
        v-model="form.toLocationID"
        @update:model-value="errorText = ''" />

      <template #buttons>
        <UiBaseButton
          :text="$t('W: Videresend')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          v-if="canUser('orders.registerSamples')"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useToast } from '@/globals/typescript/utils/toast'
import { useAppStore } from '@/stores/appStore'
import { useLabLocationStore } from '@/stores/labLocationStore'
import { useModalStore } from '@/stores/modalStore'
import { useOrderStore } from '@/stores/orderStore'
import { useUserStore } from '@/stores/userStore'
import { mapActions, mapState } from 'pinia'
import type { PropType } from 'vue'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'ForwardSamplesModal',

  emits: ['close'],

  props: {
    orderID: { type: String as PropType<string>, required: true },
    sampleIDs: { type: Array as PropType<string[]>, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        fromLocationID: '',
        toLocationID: '',
      },
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['defaultMessageSaveIconTime']),
    ...mapState(useUserStore, ['currentUser']),
    ...mapState(useLabLocationStore, ['activeLabLocations']),

    locationOptions(): { value: string; text: string }[] {
      return this.activeLabLocations
        .map((x) => ({ value: x.id, text: x.name }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    toLocationOptions(): { value: string; text: string }[] {
      return this.locationOptions.filter(
        (x) => x.value !== this.form.fromLocationID
      )
    },
  },

  methods: {
    ...mapActions(useModalStore, ['closeTop']),
    ...mapActions(useUserStore, ['canUser']),
    ...mapActions(useOrderStore, ['forwardSamples']),

    async onSubmit(): Promise<void> {
      if (!this.canUser('orders.registerSamples')) return

      this.isLoading = true
      this.errorText = ''
      try {
        await this.forwardSamples(
          this.orderID,
          this.sampleIDs,
          this.form.fromLocationID,
          this.form.toLocationID
        )
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        useToast().success(this.$t('W: Prøver videresendt'))
        this.closeTop()
      } catch {
        useToast().error(this.$t('W: Kunne ikke videresende prøver'))
        this.errorText = this.$t('error.common.standard')
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    this.form.fromLocationID = this.currentUser?.assignedLocationID ?? ''
    // Auto-fill "to" when exactly one other active location is eligible.
    if (this.toLocationOptions.length === 1) {
      this.form.toLocationID = this.toLocationOptions[0]!.value
    }
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
